import { z } from 'zod';
import { TeamSizeSchema, UseCaseSchema } from './studio.js';

// ---------------------------------------------------------------------------
// Auth form schemas
// All boundary inputs from auth forms are validated here before being passed
// to Supabase. These schemas are the single source of truth for validation
// rules; the UI reads error messages from Zod so they stay consistent.
// ---------------------------------------------------------------------------

export const SignUpEmailFormSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8).max(128),
});
export type SignUpEmailForm = z.infer<typeof SignUpEmailFormSchema>;

// Login does not enforce the min-length guard - Supabase is authoritative for
// credentials and should not reject users who created accounts before tighter
// rules were applied.
export const SignInEmailFormSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1).max(128),
});
export type SignInEmailForm = z.infer<typeof SignInEmailFormSchema>;

export const ForgotPasswordFormSchema = z.object({
  email: z.string().email(),
});
export type ForgotPasswordForm = z.infer<typeof ForgotPasswordFormSchema>;

export const ResetPasswordFormSchema = z
  .object({
    password: z.string().min(8).max(128),
    confirmPassword: z.string(),
  })
  .superRefine((data, ctx) => {
    if (data.password !== data.confirmPassword) {
      ctx.addIssue({
        code: 'custom',
        path: ['confirmPassword'],
        message: 'Passwords must match',
      });
    }
  });
export type ResetPasswordForm = z.infer<typeof ResetPasswordFormSchema>;

export const StudioSetupFormSchema = z.object({
  name: z.string().trim().min(1).max(80),
  teamSize: TeamSizeSchema,
  useCase: UseCaseSchema,
});
export type StudioSetupForm = z.infer<typeof StudioSetupFormSchema>;

// ---------------------------------------------------------------------------
// Studio RPC response schema
// Both create_studio_with_owner and update_studio_info return a raw studios
// row in snake_case. This schema validates and transforms to camelCase so the
// portal can pipe the result straight into auth/shell context state.
// ---------------------------------------------------------------------------

const StudioRowSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  short_id: z.string(),
  team_size: TeamSizeSchema,
  use_case: UseCaseSchema,
  stripe_customer_id: z.string().nullable(),
  subscription_status: z
    .enum(['trialing', 'active', 'past_due', 'canceled', 'incomplete'])
    .nullable(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
});

export const StudioRpcResponseSchema = StudioRowSchema.transform((row) => ({
  id: row.id,
  name: row.name,
  slug: row.slug,
  shortId: row.short_id,
  teamSize: row.team_size,
  useCase: row.use_case,
  stripeCustomerId: row.stripe_customer_id,
  subscriptionStatus: row.subscription_status,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
}));
export type StudioRpcResponse = z.infer<typeof StudioRpcResponseSchema>;

// ---------------------------------------------------------------------------
// Discord user metadata schema
// Discord OAuth tokens carry a user_metadata object whose shape is not
// guaranteed. We validate only the fields we care about and passthrough the
// rest so we do not reject unfamiliar keys.
// ---------------------------------------------------------------------------

export const DiscordUserMetadataSchema = z
  .object({
    full_name: z.string().optional(),
    name: z.string().optional(),
    user_name: z.string().optional(),
    preferred_username: z.string().optional(),
    email: z.string().email().optional(),
  })
  .passthrough();
export type DiscordUserMetadata = z.infer<typeof DiscordUserMetadataSchema>;

// ---------------------------------------------------------------------------
// defaultStudioName helper
// Derives a sensible default studio name from a Supabase user object so the
// /welcome form can pre-populate the name field.
//
// Priority order:
//   1. user_metadata keys: full_name, name, user_name, preferred_username
//   2. Email local-part
//   3. 'Your Studio' (returned as-is - no "'s Studio" suffix)
// ---------------------------------------------------------------------------

export function defaultStudioName(user: {
  email?: string | null;
  user_metadata?: Record<string, unknown> | null;
}): string {
  const meta = DiscordUserMetadataSchema.partial().safeParse(user.user_metadata ?? {});

  if (meta.success) {
    const derived =
      meta.data.full_name ??
      meta.data.name ??
      meta.data.user_name ??
      meta.data.preferred_username;

    if (typeof derived === 'string' && derived.trim().length > 0) {
      return `${derived.trim()}'s Studio`;
    }
  }

  if (typeof user.email === 'string' && user.email.includes('@')) {
    const localPart = user.email.split('@')[0];
    if (localPart && localPart.length > 0) {
      return `${localPart}'s Studio`;
    }
  }

  return 'Your Studio';
}
